import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import CopyButton from '../molecules/CopyButton'
import {
  setRawInput,
  formatJson,
  minifyJson,
  validateJson,
  convertToCsv,
  convertToXml,
  setViewMode,
  clearJsonState,
} from '../store/slices/jsonSlice'
import './JsonFormatterPage.css'

const JsonFormatterPage = ({ tools = [] }) => {
  const dispatch = useDispatch()
  const { rawInput, formatted, minified, csv, xml, error, viewMode, isValid } = useSelector(state => state.json)
  const [expandedNodes, setExpandedNodes] = useState(new Set())
  const [lastUpdated, setLastUpdated] = useState(null)

  useEffect(() => {
    setLastUpdated(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    )
  }, [])

  const handleInputChange = (e) => {
    dispatch(setRawInput(e.target.value))
  }

  const handleFormat = () => {
    dispatch(formatJson())
    dispatch(setViewMode('formatted'))
  }

  const handleMinify = () => {
    dispatch(minifyJson())
    dispatch(setViewMode('minified'))
  }

  const handleValidate = () => {
    dispatch(validateJson())
  }

  const handleToCsv = () => {
    dispatch(convertToCsv())
    dispatch(setViewMode('csv'))
  }

  const handleToXml = () => {
    dispatch(convertToXml())
    dispatch(setViewMode('xml'))
  }

  const handleClear = () => {
    dispatch(clearJsonState())
    setExpandedNodes(new Set())
  }

  const currentText = () => {
    switch (viewMode) {
      case 'formatted': return formatted
      case 'minified': return minified
      case 'csv': return csv
      case 'xml': return xml
      default: return formatted
    }
  }

  const downloadFile = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleDownload = () => {
    if (viewMode === 'csv') return downloadFile(csv, 'data.csv', 'text/csv')
    if (viewMode === 'xml') return downloadFile(xml, 'data.xml', 'application/xml')
    if (viewMode === 'minified') return downloadFile(minified, 'data.min.json', 'application/json')
    return downloadFile(formatted, 'data.json', 'application/json')
  }

  const renderTreeView = (obj, path = '') => {
    if (obj === null) return <span className="json-null">null</span>
    if (typeof obj === 'boolean') return <span className="json-boolean">{String(obj)}</span>
    if (typeof obj === 'number') return <span className="json-number">{obj}</span>
    if (typeof obj === 'string') return <span className="json-string">"{obj}"</span>

    if (Array.isArray(obj)) {
      return (
        <div className="json-array">
          <span>[</span>
          {obj.length > 0 && (
            <div className="json-indent">
              {obj.map((item, index) => (
                <div key={index} className="json-item">
                  {renderTreeView(item, `${path}[${index}]`)}
                  {index < obj.length - 1 && <span>,</span>}
                </div>
              ))}
            </div>
          )}
          <span>]</span>
        </div>
      )
    }

    if (typeof obj === 'object') {
      const entries = Object.entries(obj)
      return (
        <div className="json-object">
          <span>{`{`}</span>
          {entries.length > 0 && (
            <div className="json-indent">
              {entries.map(([key, value], index) => (
                <div key={key} className="json-item">
                  <span className="json-key">"{key}"</span>
                  <span>: </span>
                  {renderTreeView(value, `${path}.${key}`)}
                  {index < entries.length - 1 && <span>,</span>}
                </div>
              ))}
            </div>
          )}
          <span>{`}`}</span>
        </div>
      )
    }

    return String(obj)
  }

  const renderErrorSnippet = () => {
    if (!error || error.line == null || error.col == null) return null
    const lines = rawInput.split('\n')
    const lineText = lines[error.line - 1]
    if (lineText == null) return null

    const idx = error.col - 1
    const WINDOW_BEFORE = 40
    const WINDOW_AFTER = 30
    const start = Math.max(0, idx - WINDOW_BEFORE)
    const end = Math.min(lineText.length, idx + WINDOW_AFTER)
    const prefix = start > 0 ? '…' : ''
    const suffix = end < lineText.length ? '…' : ''
    const snippet = prefix + lineText.slice(start, end) + suffix
    const caretPos = prefix.length + (idx - start)

    return (
      <pre className="json-error-snippet" aria-hidden="true">
        <span className="snippet-line">{snippet || ' '}</span>
        <span className="snippet-caret">{' '.repeat(Math.max(0, caretPos)) + '^'}</span>
      </pre>
    )
  }

  const getOutputContent = () => {
    if (!rawInput.trim()) return null

    if (viewMode === 'tree' && isValid) {
      try {
        const parsed = JSON.parse(rawInput)
        return (
          <div className="json-tree">
            {renderTreeView(parsed)}
          </div>
        )
      } catch (e) {
        return null
      }
    }

    if (viewMode === 'formatted' && formatted) {
      return <pre className="json-code"><code>{formatted}</code></pre>
    }

    if (viewMode === 'minified' && minified) {
      return <pre className="json-code"><code>{minified}</code></pre>
    }

    if (viewMode === 'csv' && csv) {
      return <pre className="json-code"><code>{csv}</code></pre>
    }

    if (viewMode === 'xml' && xml) {
      return <pre className="json-code"><code>{xml}</code></pre>
    }

    return null
  }

  const hasOutput = Boolean(getOutputContent())

  return (
    <ToolPageTemplate path="/json-formatter/" tools={tools}>
      <div>
        <section className="json-formatter">
          <div className="json-input-section">
            <Input
              as="textarea"
              label="JSON Input"
              value={rawInput}
              onChange={handleInputChange}
              placeholder="Paste your JSON here..."
              rows={10}
              style={{ resize: 'vertical', fontFamily: 'var(--font-mono)' }}
            />

            <div className="json-info">
              {rawInput.trim() ? (
                <span className={`validation-status ${isValid ? 'valid' : 'invalid'}`}>
                  {isValid ? '✓ Valid JSON' : '✗ Invalid JSON'}
                </span>
              ) : (
                <span className="validation-status">Waiting for input</span>
              )}
              <span className="char-count">
                {rawInput.length} characters
              </span>
            </div>

            {error && (
              <div className="json-error" role="alert">
                <div className="json-error-message">
                  {(error.line != null && error.col != null) && (
                    <span className="error-location">Line {error.line}, Column {error.col}: </span>
                  )}
                  {error.message}
                </div>
                {renderErrorSnippet()}
              </div>
            )}

            <div className="json-buttons">
              <Button onClick={handleFormat}>Format</Button>
              <Button variant="secondary" onClick={handleMinify}>Minify</Button>
              <Button variant="secondary" onClick={handleValidate}>Validate</Button>
              <Button variant="secondary" onClick={handleToCsv}>To CSV</Button>
              <Button variant="secondary" onClick={handleToXml}>To XML</Button>
              <Button variant="ghost" onClick={handleClear}>Clear</Button>
            </div>
          </div>

          {hasOutput && (
            <ResultBox title="Output">
              <div className="json-output-section">
                <div className="view-mode-tabs">
                  <button
                    className={`mode-tab ${viewMode === 'formatted' ? 'active' : ''}`}
                    onClick={() => dispatch(setViewMode('formatted'))}
                    disabled={!formatted}
                  >
                    Formatted
                  </button>
                  <button
                    className={`mode-tab ${viewMode === 'minified' ? 'active' : ''}`}
                    onClick={() => dispatch(setViewMode('minified'))}
                    disabled={!minified}
                  >
                    Minified
                  </button>
                  <button
                    className={`mode-tab ${viewMode === 'tree' ? 'active' : ''}`}
                    onClick={() => dispatch(setViewMode('tree'))}
                    disabled={!isValid}
                  >
                    Tree View
                  </button>
                  <button
                    className={`mode-tab ${viewMode === 'csv' ? 'active' : ''}`}
                    onClick={() => dispatch(setViewMode('csv'))}
                    disabled={!csv}
                  >
                    CSV
                  </button>
                  <button
                    className={`mode-tab ${viewMode === 'xml' ? 'active' : ''}`}
                    onClick={() => dispatch(setViewMode('xml'))}
                    disabled={!xml}
                  >
                    XML
                  </button>
                </div>

                <div className="json-output">
                  {getOutputContent()}
                </div>

                {viewMode !== 'tree' && currentText() && (
                  <div className="json-output-actions">
                    <CopyButton text={currentText()}>Copy</CopyButton>
                    <Button variant="secondary" onClick={handleDownload}>Download</Button>
                  </div>
                )}
              </div>
            </ResultBox>
          )}
        </section>

        <section className="tool-guide">
          {/* NEW: Enhanced definition */}
          <div className="guide-section">
            <h2>What Is JSON?</h2>
            <p>JSON (JavaScript Object Notation) is a lightweight, text-based format for storing and exchanging structured data. It was specified by Douglas Crockford in the early 2000s and standardized as ECMA-404 in 2013 and RFC 8259 in 2017. Despite its name, JSON is language-independent — every mainstream programming language has a parser for it, which is why it became the universal format for web APIs, configuration files, and data interchange.</p>
            <p>JSON has only six data types: <strong>string</strong> (double-quoted text), <strong>number</strong> (integer or decimal, no quotes), <strong>boolean</strong> (<code>true</code> / <code>false</code>), <strong>null</strong>, <strong>array</strong> (ordered list in square brackets), and <strong>object</strong> (key-value pairs in curly braces). Because everything is built from these six primitives, JSON parsers are small, fast, and easy to implement correctly — which is a major reason it won out over XML for modern APIs.</p>
            <p>A minimal valid JSON file can be as short as <code>{`{"ok": true}`}</code>. There is no requirement that JSON contain objects at the top level — a bare array like <code>[1, 2, 3]</code> is also valid JSON. What it cannot contain is JavaScript-specific syntax: no comments, no trailing commas, no single-quoted strings, no functions, and no <code>undefined</code> values. Every one of those is a common source of the errors this formatter detects.</p>
          </div>

          {/* NEW: JSON vs XML vs YAML comparison */}
          <div className="guide-section">
            <h2>JSON vs XML vs YAML — When Each Format Wins</h2>
            <div className="use-cases">
              <div className="use-case">
                <h3>JSON</h3>
                <p><strong>Best for:</strong> REST APIs, browser-server communication, NoSQL databases, application config. Human-readable, universally supported, no schema required. Loses to XML for documents with mixed content and attributes; loses to YAML for hand-edited configuration with comments.</p>
              </div>
              <div className="use-case">
                <h3>XML</h3>
                <p><strong>Best for:</strong> document-centric data, SOAP and legacy enterprise systems, formats that need attributes, namespaces, and rich schema validation. More verbose than JSON. Still dominant in publishing (RSS, sitemaps), finance (FIX), and healthcare (HL7) integrations.</p>
              </div>
              <div className="use-case">
                <h3>YAML</h3>
                <p><strong>Best for:</strong> human-edited configuration files (Kubernetes manifests, CI pipelines, Ansible playbooks). Supports comments and multi-line strings. Whitespace-sensitive, which makes it fragile for machine-generated output. Not a replacement for JSON in APIs.</p>
              </div>
            </div>
          </div>

          {/* NEW: Common JSON errors with fix examples */}
          <div className="guide-section">
            <h2>Common JSON Errors (and How to Fix Them)</h2>
            <p>These are the syntax problems that cause most JSON validation failures — every one of them is caught by the live validator above with the exact line and column:</p>
            <div className="use-cases">
              <div className="use-case">
                <h3>Trailing comma</h3>
                <p><strong>Invalid:</strong> <code>{`{"name": "John", "age": 30,}`}</code><br /><strong>Fix:</strong> remove the comma after the last item — <code>{`{"name": "John", "age": 30}`}</code>.</p>
              </div>
              <div className="use-case">
                <h3>Single-quoted strings</h3>
                <p><strong>Invalid:</strong> <code>{`{'name': 'John'}`}</code><br /><strong>Fix:</strong> JSON requires double quotes — <code>{`{"name": "John"}`}</code>.</p>
              </div>
              <div className="use-case">
                <h3>Unquoted object keys</h3>
                <p><strong>Invalid:</strong> <code>{`{name: "John"}`}</code><br /><strong>Fix:</strong> wrap keys in double quotes — <code>{`{"name": "John"}`}</code>.</p>
              </div>
              <div className="use-case">
                <h3>Unescaped control characters</h3>
                <p><strong>Invalid:</strong> <code>{`{"note": "line1\nline2"}`}</code> where the newline is a literal character.<br /><strong>Fix:</strong> use the two-character escape <code>{`\\n`}</code> inside the string.</p>
              </div>
              <div className="use-case">
                <h3>Comments</h3>
                <p><strong>Invalid:</strong> <code>{`{ // user data\n "name": "John" }`}</code><br /><strong>Fix:</strong> JSON has no comment syntax. Move comments outside the file, or use JSON5/JSONC where supported.</p>
              </div>
              <div className="use-case">
                <h3>NaN and Infinity</h3>
                <p><strong>Invalid:</strong> <code>{`{"value": NaN}`}</code><br /><strong>Fix:</strong> JSON doesn't support these. Use <code>null</code> or a string like <code>"NaN"</code>.</p>
              </div>
            </div>
          </div>

          {/* EXISTING: How the formatter works (kept, tightened) */}
          <div className="guide-section">
            <h2>How This JSON Formatter Works</h2>
            <p>The formatter uses JavaScript's built-in <code>JSON.parse()</code> — the same parser every browser ships — to validate input and build an in-memory representation of your data. If parsing fails, the tool catches the thrown error, extracts the character position from the error message, and converts that position into the exact line and column you see in the error output above. This is why error locations are precise even for deeply nested JSON.</p>
            <p>Formatting (<code>JSON.stringify(data, null, 2)</code>) re-serializes the parsed data with 2-space indentation at every nesting level. Minifying uses <code>JSON.stringify(data)</code> without indentation, producing the smallest valid JSON string. Both operations preserve key order, number precision, and string escaping exactly as the JSON specification requires.</p>
            <p>CSV conversion flattens an array of objects into rows and columns; nested keys become dot-notation headers like <code>address.city</code>. XML conversion produces well-formed XML with properly escaped characters. Neither format is lossless for arbitrary JSON — for example, a top-level array of primitives cannot become a CSV table — and the tool will tell you when a conversion isn't possible.</p>
            <p>Every operation runs entirely in your browser. Nothing is uploaded, nothing is logged, and closing the tab erases everything — which matters when the JSON you're formatting contains API keys, customer data, or configuration secrets.</p>
          </div>

          <div className="guide-section">
            <h2>How to Use This JSON Formatter</h2>
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Paste your JSON</h3>
                  <p>Copy JSON from an API response, config file, log, or database export and paste it into the input area. Validation runs live as you type — the status indicator tells you immediately whether the JSON is valid, and if not, exactly where the first syntax error is.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Choose an action</h3>
                  <p>Click Format to beautify, Minify to compress, Validate to check syntax only, or To CSV / To XML to convert. Formatting and minifying both require valid JSON — the tool disables the button if the input can't be parsed.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Switch view modes</h3>
                  <p>Use the tabs above the output to move between Formatted, Minified, Tree View, CSV, and XML. Tree View is interactive and best for exploring nested structures. Formatted and Minified are best for copying back into a file or API request.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Copy or download</h3>
                  <p>Click Copy to send the current view to your clipboard, or Download to save it as a .json, .csv, or .xml file. Everything happens locally — no upload, no logging, no retention.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Use Cases</h2>
            <div className="use-cases">
              <div className="use-case">
                <h3>API debugging</h3>
                <p>Paste a response body from Postman, curl, or the browser Network tab to inspect it in a readable format, spot missing fields, and confirm the exact shape of nested objects before writing code against it.</p>
              </div>
              <div className="use-case">
                <h3>Configuration file editing</h3>
                <p>Format <code>package.json</code>, <code>tsconfig.json</code>, Docker Compose files, or Kubernetes manifests for readability. Minify when you need to embed the same config in an environment variable or a command-line argument.</p>
              </div>
              <div className="use-case">
                <h3>JSON to CSV for spreadsheets</h3>
                <p>Convert an array of API results into a CSV you can open directly in Excel, Google Sheets, or any data tool. Nested keys become dot-notation columns, so a <code>user.address.city</code> field appears as a single flat column.</p>
              </div>
              <div className="use-case">
                <h3>JSON to XML for legacy integrations</h3>
                <p>Bridge modern REST APIs with older enterprise systems that still expect XML. The converter produces well-formed XML with escaped entities, ready to drop into a SOAP request or a legacy data pipeline.</p>
              </div>
              <div className="use-case">
                <h3>Validating before deployment</h3>
                <p>Catch syntax errors before pushing a config change to production. The exact line and column output makes it easy to jump straight to the problem, and the validation runs entirely offline — safe for any environment.</p>
              </div>
            </div>
          </div>

          {/* UPDATED: FAQ expanded from 5 to 11 */}
          <div className="guide-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>What's the difference between formatting and minifying JSON?</h3>
                <p>Formatting adds indentation and line breaks to make JSON human-readable, while minifying removes all unnecessary whitespace to produce the smallest possible valid file. Formatted JSON is ideal for editing and debugging; minified JSON is better for production APIs, storage, and transmission over the network.</p>
              </div>
              <div className="faq-item">
                <h3>How do I find where my JSON error is?</h3>
                <p>Paste or type your JSON and the tool validates it live. When the syntax is invalid, it shows the exact line and column of the first error, along with the caret snippet and a plain-language message. Common causes include trailing commas, single-quoted strings, unquoted keys, and missing brackets.</p>
              </div>
              <div className="faq-item">
                <h3>Can I convert JSON to CSV or XML here?</h3>
                <p>Yes. Click To CSV to flatten an array of objects into a table (nested keys become dot-notation columns), or To XML to produce well-formed XML with properly escaped characters. Both converters require valid JSON and will report if the structure can't be converted.</p>
              </div>
              <div className="faq-item">
                <h3>Is my JSON data secure when using this tool?</h3>
                <p>Yes. All parsing, formatting, and conversion happen entirely in your browser using JavaScript. Your JSON is never uploaded, logged, or stored anywhere. This matters when you're formatting config files containing API keys, customer records, or internal endpoints.</p>
              </div>
              <div className="faq-item">
                <h3>What JSON data types are supported?</h3>
                <p>JSON supports six data types: strings (in double quotes), numbers, booleans (<code>true</code>/<code>false</code>), null, arrays, and objects. The formatter preserves all of them exactly, including high-precision numbers and Unicode characters.</p>
              </div>
              <div className="faq-item">
                <h3>Why does my JSON show as invalid when it looks fine?</h3>
                <p>The most common causes are a trailing comma after the last item in an object or array, a single-quoted string instead of double-quoted, an unquoted object key, or an unescaped control character inside a string. Click the error message and check the line and column — the fix is almost always one character.</p>
              </div>
              <div className="faq-item">
                <h3>Can JSON have comments?</h3>
                <p>No. Standard JSON (per RFC 8259) does not support comments. If you need comments in a config file, use JSON5, JSONC (used by VS Code's <code>settings.json</code>), or switch to YAML. This formatter will report comments as a syntax error, matching strict JSON parsers.</p>
              </div>
              <div className="faq-item">
                <h3>What's the difference between JSON and JavaScript objects?</h3>
                <p>They look similar but are not the same. JavaScript objects can contain functions, <code>undefined</code> values, comments, single-quoted strings, and trailing commas — none of which are valid JSON. A JavaScript object literal is a piece of code; JSON is a data format. You can convert between them with <code>JSON.stringify()</code> and <code>JSON.parse()</code>.</p>
              </div>
              <div className="faq-item">
                <h3>Is there a size limit for the JSON I can paste?</h3>
                <p>No hard limit is imposed by the tool. In practice, browser performance is comfortable up to a few megabytes of JSON. For files larger than 10 MB, use a dedicated command-line tool like <code>jq</code> — the browser's single-threaded parsing becomes the bottleneck, not the formatter itself.</p>
              </div>
              <div className="faq-item">
                <h3>Does the formatter preserve key order?</h3>
                <p>Yes. JavaScript's <code>JSON.parse()</code> and <code>JSON.stringify()</code> preserve the insertion order of object keys for non-numeric keys, so the formatted output matches the original order exactly. Integer-like keys (for example, "1", "2") are sorted numerically — that's a language-level behavior, not a formatter choice.</p>
              </div>
              <div className="faq-item">
                <h3>What's the difference between JSON and XML?</h3>
                <p>JSON is minimal, type-safe, and easy to parse in every language, which makes it the default for modern REST APIs. XML is more verbose but supports attributes, namespaces, mixed content, and rich schema validation (XSD), which is why it remains dominant in document-centric and enterprise integration contexts. For new APIs, JSON is almost always the better choice.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Related Tools</h2>
            <div className="related-tools">
              <Link to="/word-counter/" className="related-tool">
                <div className="tool-icon">Word Counter</div>
                <div className="tool-description">Analyze JSON string length and character counts</div>
              </Link>
              <Link to="/qr-generator/" className="related-tool">
                <div className="tool-icon">QR Generator</div>
                <div className="tool-description">Create QR codes for sharing JSON configuration data</div>
              </Link>
              <Link to="/text-to-speech/" className="related-tool">
                <div className="tool-icon">Text to Speech</div>
                <div className="tool-description">Convert JSON content to audio for accessibility</div>
              </Link>
              <Link to="/blog/what-is-json-and-why-developers-use-it/" className="related-tool">
                <div className="tool-icon">What is JSON and Why Developers Use It</div>
                <div className="tool-description">JSON fundamentals, best practices, and real-world applications</div>
              </Link>
            </div>
          </div>
        </section>

        <div className="last-updated">
          <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
        </div>
      </div>
    </ToolPageTemplate>
  )
}

export default JsonFormatterPage